/**
 * #731 — the position card's row BOUNDARY. Sibling of `OrderCardRow`; same
 * reasoning, same contract. See that file's header for why this is a component
 * rather than a keyed handler cache.
 *
 * `highlighted` is a plain boolean for the same reason: the same-game map is
 * rebuilt every publication by design, so passing anything derived from its
 * identity would rebuild every row for a highlight that did not move.
 */
import { memo, useCallback, type Dispatch, type SetStateAction } from 'react';
import { CompactPositionItem } from '../../components/cards/PositionCard';
import type { PositionCardVM } from '../../components/cards/positionCardModel';
import type { EditPositionData } from './EditPositionModal';
import type { SameGameRowRef } from './index';

export interface PositionCardRowProps {
  vm: PositionCardVM;
  highlighted: boolean;
  hidePnl: boolean;
  divergence: React.ComponentProps<typeof CompactPositionItem>['divergence'];
  divergenceFlash: boolean;
  detailHref: string | null;
  pinned: boolean;
  /** Derived once by the parent so the pin entry cannot disagree with the header. */
  label: string;
  /** #828 — stable row identity for the reading-position primitive. Reaches
   *  `CompactPositionItem` through the `...cardProps` rest spread. */
  viewAnchor?: string;
  setHoveredRow: Dispatch<SetStateAction<SameGameRowRef | null>>;
  openOrderPanel: (ticker: string, side: 'yes' | 'no' | null | undefined) => void;
  setEditPosition: Dispatch<SetStateAction<EditPositionData | null>>;
  togglePin: (entry: { key: string; kind: 'position'; label: string }) => void;
}

function PositionCardRowInner({
  vm,
  label,
  setHoveredRow,
  openOrderPanel,
  setEditPosition,
  togglePin,
  ...cardProps
}: PositionCardRowProps) {
  const onMouseEnter = useCallback(
    () => setHoveredRow({ venue: vm.venue, ticker: vm.ticker, key: vm.key }),
    [setHoveredRow, vm.venue, vm.ticker, vm.key],
  );
  const onMouseLeave = useCallback(() => setHoveredRow(null), [setHoveredRow]);
  const onOpen = useCallback(
    () => openOrderPanel(vm.ticker, vm.side),
    [openOrderPanel, vm.ticker, vm.side],
  );
  const onEdit = useCallback(
    () =>
      setEditPosition({
        ticker: vm.ticker,
        side: vm.side,
        position: vm.qty,
        pendingSellQty: vm.pendingSellQty,
        averagePrice: vm.avgCents,
        currentPriceCents: vm.nowCents,
      }),
    [setEditPosition, vm.ticker, vm.side, vm.qty, vm.pendingSellQty, vm.avgCents, vm.nowCents],
  );
  const onTogglePin = useCallback(
    () => togglePin({ key: vm.key, kind: 'position', label }),
    [togglePin, vm.key, label],
  );

  return (
    <CompactPositionItem
      vm={vm}
      {...cardProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onOpen={onOpen}
      onEdit={onEdit}
      onTogglePin={onTogglePin}
    />
  );
}

export const PositionCardRow = memo(PositionCardRowInner);
